import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useThemeContext } from '../context/ThemeProvider';
import { DRUM_SAMPLES } from '../config/drumSamples';
import { getSmartInstrumentId, getInstrumentConfigById } from '../utils/smartInstrumentId';
import { DRUM_TYPES, PRESET_DRUMSETS } from '../config/drumKits';
import { FaVolumeUp, FaPlus, FaMinus, FaChevronDown, FaChevronRight, FaSave, FaTimes, FaUpload, FaTrash } from 'react-icons/fa';
import type { DrumInstrument, DrumsetConfig } from '../types';

interface SoundManagementPanelProps {
  onClose: () => void;
  onSaveKit?: (instruments: DrumInstrument[], kitName?: string) => void;
  showDynamicMessage?: (message: string, type?: "info" | "success" | "warning" | "error") => void;
  currentInstruments?: DrumInstrument[]; // 從現有鼓組讀取
  drumsetConfig?: DrumsetConfig | null; // 從配置讀取
}

interface SelectedSample {
  id: string;
  name: string;
  file: string;
  type: string;
  color?: string;
  instrumentId?: string;
  instrumentName?: string;
}

interface SavedKitData {
  selectedSamples: SelectedSample[];
  kitName: string;
  expandedCategories: string[];
  timestamp: number;
}

export default function SoundManagementPanel({ 
  onClose, 
  onSaveKit, 
  showDynamicMessage,
  currentInstruments,
  drumsetConfig
}: SoundManagementPanelProps) {
  const { t } = useLanguage();
  const { getClass } = useThemeContext();
  
  const getThemeClass = (type: string): string => {
    try {
      return getClass(type) || '';
    } catch {
      const fallback: Record<string, string> = {
        background: 'bg-white',
        text: 'text-gray-900',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        grid: 'bg-gray-100',
        border: 'border-gray-300',
        input: 'border-gray-300 bg-white text-gray-900',
        card: 'bg-white border-gray-200 shadow-sm',
      };
      return fallback[type] || '';
    }
  };

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedSamples, setSelectedSamples] = useState<SelectedSample[]>([]);
  const [kitName, setKitName] = useState<string>('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // 從currentInstruments轉換為SelectedSample格式
  const convertInstrumentsToSamples = (instruments: DrumInstrument[]): SelectedSample[] => {
    const validSamples: SelectedSample[] = [];
    
          for (const instrument of instruments) {
        // 在DRUM_SAMPLES中查找對應的sample
        let found = false;
        for (const [category, samples] of Object.entries(DRUM_SAMPLES)) {
          const sample = samples.find(s => s.id === instrument.sampleId);
          if (sample) {
            validSamples.push({
              id: sample.id,
              name: sample.name,
              file: sample.file,
              type: category,
              color: instrument.color,
              instrumentId: instrument.id,
              instrumentName: instrument.name
            });
            found = true;
            break;
          }
        }
        
        // 如果找不到確切匹配，嘗試按類型找一個替代品
        if (!found) {
          for (const [category, samples] of Object.entries(DRUM_SAMPLES)) {
            if (category === instrument.type && samples.length > 0) {
              const fallbackSample = samples[0]; // 使用該類型的第一個sample
              validSamples.push({
                id: fallbackSample.id,
                name: fallbackSample.name,
                file: fallbackSample.file,
                type: category,
                color: instrument.color,
                instrumentId: instrument.id,
                instrumentName: instrument.name
              });
              break;
            }
          }
        }
      }
    
    return validSamples;
  };

  // 組件掛載時載入數據（僅載入一次，使用簡化邏輯）
  useEffect(() => {
    // 只嘗試從localStorage讀取上次保存的設定
    try {
      const savedData = localStorage.getItem('soundManagementKit');
      if (savedData) {
        const parsedData: SavedKitData = JSON.parse(savedData);
        if (parsedData.selectedSamples && parsedData.selectedSamples.length > 0) {
          setSelectedSamples(parsedData.selectedSamples);
          setKitName(parsedData.kitName || '');
          setExpandedCategories(new Set(parsedData.expandedCategories || []));
          showDynamicMessage?.(t('loadedPreviousKit') || 'Loaded previous kit configuration', 'info');
          return;
        }
      }
    } catch (error) {
      console.error('Error loading saved kit data:', error);
    }

    // 如果沒有保存的數據，設置為空白狀態
    setSelectedSamples([]);
    setKitName(t('customKit') || 'Custom Kit');
    setExpandedCategories(new Set());
  }, []); // 只在組件掛載時執行一次

  // 保存到localStorage
  const saveToLocalStorage = (samples: SelectedSample[], name: string, expanded: string[]) => {
    try {
      const dataToSave: SavedKitData = {
        selectedSamples: samples,
        kitName: name,
        expandedCategories: expanded,
        timestamp: Date.now()
      };
      localStorage.setItem('soundManagementKit', JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving kit data:', error);
    }
  };

  const toggleCategory = (category: string): void => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
    
    // 保存展開狀態到localStorage
    saveToLocalStorage(selectedSamples, kitName, Array.from(newExpanded));
  };

  const playSample = async (sampleFile: string): Promise<void> => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      const audio = new Audio(sampleFile);
      audioRef.current = audio;
      await audio.play();
    } catch (error) {
      console.error('Error playing sample:', error);
      showDynamicMessage?.(t('playSample') + ' failed', 'error');
    }
  };

  const addToKit = (sample: { id: string; name: string; file: string }, type: string): void => {
    // 生成智能ID和預設配色
    const existingInstrumentIds = selectedSamples.map(s => s.instrumentId || '').filter(id => id);
    const smartId = getSmartInstrumentId(type as 'kick' | 'snare' | 'hihat' | 'openhat' | 'tom' | 'clap' | 'crash' | 'ride' | 'percussion', existingInstrumentIds);
    const drumType = DRUM_TYPES.find(dt => dt.id === type);
    
    const newSample: SelectedSample = {
      id: sample.id,
      name: sample.name, // 使用音檔名稱
      file: sample.file,
      type,
      color: drumType?.color || 'bg-gray-500',
      instrumentId: smartId,
      instrumentName: sample.name // 樂器名稱也使用音檔名稱
    };
    
    if (!selectedSamples.find(s => s.id === sample.id)) {
      const newSamples = [...selectedSamples, newSample];
      setSelectedSamples(newSamples);
      showDynamicMessage?.(t('addToKit') + ': ' + sample.name, 'success');
      
      // 保存到localStorage
      saveToLocalStorage(newSamples, kitName, Array.from(expandedCategories));
    }
  };

  const removeFromKit = (sampleId: string): void => {
    const sample = selectedSamples.find(s => s.id === sampleId);
    const newSamples = selectedSamples.filter(s => s.id !== sampleId);
    setSelectedSamples(newSamples);
    if (sample) {
      showDynamicMessage?.(t('removeFromKit') + ': ' + sample.name, 'info');
    }
    
    // 保存到localStorage
    saveToLocalStorage(newSamples, kitName, Array.from(expandedCategories));
  };

  const handleKitNameChange = (name: string): void => {
    setKitName(name);
    // 保存到localStorage
    saveToLocalStorage(selectedSamples, name, Array.from(expandedCategories));
  };

  const saveKit = (): void => {
    if (selectedSamples.length === 0) {
      showDynamicMessage?.(t('pleaseAddInstruments'), 'warning');
      return;
    }

    if (!kitName.trim()) {
      showDynamicMessage?.(t('kitName') + ' ' + t('required'), 'warning');
      return;
    }

    const instruments: DrumInstrument[] = selectedSamples.map((sample) => {
      return {
        id: sample.instrumentId || sample.id,
        name: sample.instrumentName || sample.name,
        type: sample.type,
        sampleId: sample.id,
        color: sample.color || 'bg-gray-500'
      };
    });

    // 保存最終配置到localStorage
    saveToLocalStorage(selectedSamples, kitName.trim(), Array.from(expandedCategories));

    onSaveKit?.(instruments, kitName.trim());
    showDynamicMessage?.(t('saveKit') + ': ' + kitName, 'success');
    onClose();
  };

  const loadFromCurrentKit = (): void => {
    if (currentInstruments && currentInstruments.length > 0) {
      const samples = convertInstrumentsToSamples(currentInstruments);
      setSelectedSamples(samples);
      setKitName(t('currentKit') || 'Current Kit');
      showDynamicMessage?.(t('loadedCurrentKit') || 'Loaded current kit', 'success');
      
      // 保存到localStorage
      saveToLocalStorage(samples, t('currentKit') || 'Current Kit', Array.from(expandedCategories));
    } else {
      showDynamicMessage?.(t('noCurrentKit') || 'No current kit to load', 'warning');
    }
  };

  const clearKit = (): void => {
    setSelectedSamples([]);
    setKitName(t('customKit') || 'Custom Kit');
    showDynamicMessage?.(t('kitCleared') || 'Kit cleared', 'info');
    
    // 保存空狀態到localStorage
    saveToLocalStorage([], t('customKit') || 'Custom Kit', Array.from(expandedCategories));
  };

  // 拖拽排序函數
  const handleDragStart = (index: number): void => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number): void => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    
    const newSamples = [...selectedSamples];
    const draggedSample = newSamples[draggedIndex];
    newSamples.splice(draggedIndex, 1);
    newSamples.splice(dropIndex, 0, draggedSample);
    
    // 重新生成智能ID順序
    const reorderedSamples = newSamples.map((sample, index) => {
      const typeCount = newSamples.slice(0, index).filter(s => s.type === sample.type).length;
      const existingIds = newSamples.slice(0, index).map(s => s.instrumentId || '').filter(id => id);
      const newId = getSmartInstrumentId(sample.type as 'kick' | 'snare' | 'hihat' | 'openhat' | 'tom' | 'clap' | 'crash' | 'ride' | 'percussion', existingIds);
      return { ...sample, instrumentId: newId };
    });
    
    setSelectedSamples(reorderedSamples);
    setDraggedIndex(null);
    saveToLocalStorage(reorderedSamples, kitName, Array.from(expandedCategories));
  };

  const loadPreset = (presetKey: string): void => {
    if (!presetKey) {
      setSelectedPreset('');
      return;
    }
    
    const preset = PRESET_DRUMSETS[presetKey as keyof typeof PRESET_DRUMSETS];
    if (preset) {
      setSelectedPreset(presetKey);
      setKitName(t(`drumKits.${presetKey}`) || preset.nameKey);
      
      // 轉換preset instruments為SelectedSample格式
      const presetSamples = convertInstrumentsToSamples(preset.instruments);
      setSelectedSamples(presetSamples);
      
      saveToLocalStorage(presetSamples, t(`drumKits.${presetKey}`) || preset.nameKey, Array.from(expandedCategories));
      showDynamicMessage?.(t('presetLoaded') + ': ' + (t(`drumKits.${presetKey}`) || preset.nameKey), 'success');
    }
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4`}>
      <div className={`w-[95%] max-w-6xl h-[85%] ${getThemeClass('background')} rounded-lg shadow-xl flex flex-col`}>
        {/* Header */}
        <div className={`p-4 border-b ${getThemeClass('border')} flex items-center justify-between`}>
          <h2 className={`text-xl font-bold ${getThemeClass('text')}`}>
            🎵 {t('soundManagement')}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded hover:bg-gray-200 transition-colors`}
          >
            <FaTimes className={getThemeClass('text')} />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Sample Browser */}
          <div className={`w-2/5 p-4 border-r ${getThemeClass('border')} overflow-y-auto`}>
            <h3 className={`text-lg font-semibold mb-4 ${getThemeClass('text')}`}>
              📂 {t('sampleBrowser')}
            </h3>
            
            {Object.entries(DRUM_SAMPLES).map(([category, samples]) => (
              <div key={category} className="mb-4">
                <button
                  onClick={() => toggleCategory(category)}
                  className={`w-full flex items-center justify-between p-3 rounded ${getThemeClass('card')} ${getThemeClass('border')} hover:bg-gray-50 transition-colors`}
                >
                  <span className={`font-medium ${getThemeClass('text')}`}>
                    {t(`drumTypes.${category}`) || category} ({samples.length})
                  </span>
                  {expandedCategories.has(category) ? (
                    <FaChevronDown className={getThemeClass('text')} />
                  ) : (
                    <FaChevronRight className={getThemeClass('text')} />
                  )}
                </button>
                
                {expandedCategories.has(category) && (
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1">
                    {samples.map((sample) => (
                      <div
                        key={sample.id}
                        className={`flex items-center justify-between p-1.5 rounded text-xs ${getThemeClass('grid')} hover:bg-gray-100 transition-colors`}
                      >
                        <span className={`${getThemeClass('text')} flex-1 truncate pr-1`} title={sample.name}>
                          {sample.name}
                        </span>
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          <button
                            onClick={() => playSample(sample.file)}
                            className={`p-0.5 rounded hover:bg-blue-100 transition-colors`}
                            title={t('playSample')}
                          >
                            <FaVolumeUp className="text-blue-600 text-xs" />
                          </button>
                          <button
                            onClick={() => addToKit(sample, category)}
                            className={`p-0.5 rounded hover:bg-green-100 transition-colors`}
                            title={t('addToKit')}
                          >
                            <FaPlus className="text-green-600 text-xs" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Panel - Selected Samples */}
          <div className="w-3/5 p-4 flex flex-col">
            <h3 className={`text-lg font-semibold mb-4 ${getThemeClass('text')}`}>
              🎛️ {t('selectedSamples')} ({selectedSamples.length})
            </h3>
            
            {/* Preset Kits Selector */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${getThemeClass('text')}`}>
                🥁 {t('presetKits')}
              </label>
              <select
                value={selectedPreset}
                onChange={(e) => loadPreset(e.target.value)}
                className={`w-full px-3 py-2 rounded ${getThemeClass('input')} ${getThemeClass('border')} mb-2`}
              >
                <option value="">{t('selectPresetKit') || 'Select Preset Kit...'}</option>
                {Object.entries(PRESET_DRUMSETS).map(([key, preset]) => (
                  <option key={key} value={key}>
                    {t(`drumKits.${key}`)} ({preset.instruments.length})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Kit Name Input */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${getThemeClass('text')}`}>
                🎹 {t('kitName')}
              </label>
              <input
                type="text"
                value={kitName}
                onChange={(e) => handleKitNameChange(e.target.value)}
                className={`w-full px-3 py-2 rounded ${getThemeClass('input')} ${getThemeClass('border')}`}
                placeholder={t('createNewKit')}
              />
            </div>

            {/* Selected Samples List */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {selectedSamples.length === 0 ? (
                <div className={`text-center py-8 ${getThemeClass('text')} opacity-50`}>
                  {t('selectedSamplesEmpty')}
                </div>
              ) : (
                selectedSamples.map((sample, index) => (
                  <div
                    key={sample.id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`p-2 md:p-3 rounded-lg border-2 ${sample.color || 'bg-gray-500'} bg-opacity-20 cursor-move hover:shadow-md transition-all ${
                      draggedIndex === index ? 'opacity-50' : ''
                    } ${getThemeClass('border')}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0">
                        {/* 拖拽指示 */}
                        <div className="text-gray-400 cursor-move text-sm md:text-lg">⋮⋮</div>
                        
                        {/* BD圓點 */}
                        <div 
                          className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-bold text-white border-2 border-white shadow-md ${sample.color || 'bg-gray-500'} flex-shrink-0`}
                          title={`${sample.instrumentId}: ${sample.type}`}
                        >
                          {sample.instrumentId}
                        </div>
                        
                        {/* ID和名稱 */}
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium ${getThemeClass('text')} truncate text-sm md:text-base`}>
                            {/* <span className="font-mono font-bold">{sample.instrumentId}</span> 
                            <span className="mx-1">/</span>*/}
                            <span>{sample.name}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <button
                          onClick={() => playSample(sample.file)}
                          className={`p-1.5 md:p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors`}
                          title={t('playSample')}
                        >
                          <FaVolumeUp className="text-blue-600 text-sm" />
                        </button>
                        <button
                          onClick={() => removeFromKit(sample.id)}
                          className={`p-1.5 md:p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors`}
                          title={t('removeFromKit')}
                        >
                          <FaMinus className="text-red-600 text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Action Buttons in Right Panel */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex flex-wrap gap-2 justify-center">
                {currentInstruments && currentInstruments.length > 0 && (
                  <button
                    onClick={loadFromCurrentKit}
                    className={`px-2 py-1.5 md:px-3 md:py-2 rounded ${getThemeClass('button')} transition-colors text-xs md:text-sm flex items-center space-x-1`}
                    title={t('loadCurrentKit') || 'Load Current Kit'}
                  >
                    <FaUpload className="text-xs" />
                    <span className="hidden lg:inline">{t('load') || 'Load'}</span>
                  </button>
                )}
                <button
                  onClick={clearKit}
                  className={`px-2 py-1.5 md:px-3 md:py-2 rounded bg-red-600 hover:bg-red-700 text-white transition-colors text-xs md:text-sm flex items-center space-x-1`}
                  title={t('clearKit') || 'Clear Kit'}
                >
                  <FaTrash className="text-xs" />
                  <span className="hidden lg:inline">{t('clear') || 'Clear'}</span>
                </button>
                <button
                  onClick={saveKit}
                  disabled={selectedSamples.length === 0 || !kitName.trim()}
                  className={`px-2 py-1.5 md:px-4 md:py-2 rounded font-semibold transition-colors text-xs md:text-sm flex items-center space-x-1 ${
                    selectedSamples.length === 0 || !kitName.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  <FaSave className="text-xs" />
                  <span className="hidden md:inline">{t('saveKit')}</span>
                </button>
                <button
                  onClick={onClose}
                  className={`px-2 py-1.5 md:px-3 md:py-2 rounded bg-gray-600 hover:bg-gray-700 text-white transition-colors text-xs md:text-sm flex items-center space-x-1`}
                >
                  <FaTimes className="text-xs" />
                  <span className="hidden lg:inline">{t('cancel') || 'Cancel'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 